import { history, useIntl, useModel } from '@umijs/max';
import { message } from 'antd';
import { useEffect } from 'react';
import { flushSync } from 'react-dom';
import { authService, LAST_ACTIVITY_KEY } from '@/services/authService';

const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'wheel'];
const ACTIVITY_WRITE_INTERVAL_MS = 5000;
const CHECK_INTERVAL_MS = 15000;

/**
 * 员工设置了自动登出时间时，超过该时间无操作则自动登出。
 * 最后操作时间存在 localStorage 中，多个标签页共享。
 */
const IdleLogout: React.FC = () => {
  const intl = useIntl();
  const { initialState, setInitialState } = useModel('@@initialState');
  const autoLogoutMinutes = Number(
    (initialState?.currentUser as { autoLogoutMinutes?: number | null } | undefined)?.autoLogoutMinutes,
  ) || 0;

  useEffect(() => {
    if (!autoLogoutMinutes) return undefined;
    const timeoutMs = autoLogoutMinutes * 60 * 1000;
    let lastWrite = 0;

    const readLastActivity = () => Number(localStorage.getItem(LAST_ACTIVITY_KEY)) || 0;
    const markActive = () => {
      const now = Date.now();
      if (now - lastWrite < ACTIVITY_WRITE_INTERVAL_MS) return;
      lastWrite = now;
      localStorage.setItem(LAST_ACTIVITY_KEY, String(now));
    };
    const checkIdle = () => {
      if (!authService.isAuthenticated()) return;
      const lastActivity = readLastActivity();
      if (lastActivity && Date.now() - lastActivity < timeoutMs) return;
      authService.logout();
      flushSync(() => {
        setInitialState((s) => ({ ...s, currentUser: undefined }));
      });
      history.replace('/login');
      message.info(intl.formatMessage({
        id: 'pages.login.autoLoggedOut',
        defaultMessage: 'You have been logged out due to inactivity.',
      }));
    };

    if (!readLastActivity()) markActive();
    checkIdle();
    if (!authService.isAuthenticated()) return undefined;

    ACTIVITY_EVENTS.forEach((event) => window.addEventListener(event, markActive, { passive: true }));
    const timer = window.setInterval(checkIdle, CHECK_INTERVAL_MS);
    return () => {
      ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, markActive));
      window.clearInterval(timer);
    };
  }, [autoLogoutMinutes]);

  return null;
};

export default IdleLogout;
